import sunoLogoWhiteUrl from "assets/icons/suno-logo-white.svg";

/**
 * The white wordmark is 2.7KB of plain vector paths carrying exactly two
 * `fill="#fff"` attributes, which is why it is the source for both tints: swap
 * the fill and you have the dark wordmark too.
 *
 * The other asset, `suno-logo.svg`, is 161KB — a base64 PNG wrapped in a
 * `<pattern>` — so it rasterises fuzzily at frame scale and is not used here.
 */
const WHITE_FILL_PATTERN = /fill="#fff"/gi;

let sourcePromise: Promise<string> | null = null;

const loadSource = (): Promise<string> => {
  if (!sourcePromise) {
    sourcePromise = fetch(sunoLogoWhiteUrl).then((response) => {
      if (!response.ok)
        throw new Error(`Logo fetch failed: ${response.status}`);
      return response.text();
    });
    // A failed fetch must not poison every later call for the tab's lifetime
    // the way a memoised rejection would, so drop the cache on rejection.
    sourcePromise.catch(() => {
      sourcePromise = null;
    });
  }
  return sourcePromise;
};

const cache = new Map<string, Promise<HTMLImageElement | null>>();

const toImage = async (color: string): Promise<HTMLImageElement | null> => {
  try {
    const source = await loadSource();
    const tinted = source.replace(WHITE_FILL_PATTERN, `fill="${color}"`);
    const image = new Image();
    // A data URI rather than a blob/object URL: a same-origin SVG is safe in
    // principle, but a data URI can never taint the canvas, and a tainted
    // canvas fails at `toBlob` — after the user has already picked a style.
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(tinted)}`;
    await image.decode();
    return image;
  } catch {
    // The wordmark is decoration. Losing it is worth strictly less than the
    // screenshot, so the compositor draws a text fallback and carries on.
    return null;
  }
};

/**
 * The Suno wordmark as a decoded `<Image>`, tinted to `color`, or `null` if it
 * could not be loaded. Cached per colour — there are two.
 */
export default function loadSunoLogo(
  color: string
): Promise<HTMLImageElement | null> {
  const cached = cache.get(color);
  if (cached) return cached;
  const pending = toImage(color);
  cache.set(color, pending);
  return pending;
}

/** Test seam — the module-level caches outlive a single test otherwise. */
export const resetSunoLogoCache = () => {
  cache.clear();
  sourcePromise = null;
};
